import type { ActionsNetwork } from 'shared/store/actions'
import type { Dispatch } from 'redux'
import type {
    CodeTryPayloadDTO,
    DuelApiDTO,
    PageDataDTO,
    TokenResponseDTO,
    GoodieDTO,
    TPKRestaurantDTO
} from './types'
import type { RootState } from '../reducers'
import { ws } from '../ws'
import endpoints from '../endpoints'
import { handleError } from '../actions'
import { updateToken } from 'shared/store/actions'
import type { Restaurant } from 'shared/types'
import { clearSigninRedirect } from '../signin'

export type ActionTPKDuelDataResponse = {
    type: 'TPK_DUEL_RESPONSE',
    tpkToken: string,
    data: DuelApiDTO
}

export const Errors = {
    INVALID_CODE: 'INVALID_CODE',
    INVALID_TPK_TOKEN: 'INVALID_TPK_TOKEN',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    INVALID_CAPTCHA: 'INVALID_CAPTCHA',
    MISSING_EMAIL: 'MISSING_EMAIL',
    BAD_CHOICE: 'BAD_CHOICE',
    WRONG_STEP: 'WRONG_STEP',
    ALREADY_COLLECTED: 'ALREADY_COLLECTED',
    BAD_QRCODE: 'BAD_QRCODE'
}
type ErrorType = $Keys<typeof Errors>

export type ActionTPKError = {
    type: 'TPK_ERROR',
    error: ErrorType
}

export type ActionTPKPageDataResponse = {
    type: 'TPK_PAGE_DATA_RESPONSE',
    data: PageDataDTO
}

type ActionTPKReset = {
    type: 'TPK_RESET_DUEL'
}

type ActionTPKGoodiesDataResponse = {
    type: 'TPK_GOODIES_DATA_RESPONSE',
    data: GoodieDTO[]
}

type ActionTPKAllRestaurantsResponse = {
    type: 'ALL_RESTAURANTS_DATA_RESPONSE',
    data: TPKRestaurantDTO[]
}

type ActionTPKAvailabilityResponse = {
    type: 'AVAILABILITY_RESPONSE',
    data: number,
    error: ErrorType
}

export const fetchTPKDuelData = (tpkToken: string) => async (
    dispatch: Dispatch<any>
) => {
    try {
        const { data } = await ws.get(endpoints.tuPreferesKing.duel + tpkToken)
        dispatch({
            type: 'TPK_DUEL_RESPONSE',
            data,
            tpkToken
        })
    } catch (err) {
        if (err.response) {
            switch (err.response.status) {
                case 404:
                    dispatch(actionTPKError(Errors.INVALID_CODE))
                    break
                case 500:
                    dispatch(actionTPKError(Errors.INTERNAL_ERROR))
                    break
                default:
                    dispatch(actionTPKError(Errors.INTERNAL_ERROR))
            }
        } else {
            dispatch(actionTPKError(Errors.INTERNAL_ERROR))
        }
    }
}

export const fetchTPKPageData = () => async (dispatch: Dispatch<Action>) => {
    try {
        const response = await ws.get(endpoints.tuPreferesKing.pageData)
        return dispatch({
            type: 'TPK_PAGE_DATA_RESPONSE',
            data: response.data
        })
    } catch (e) {
        if (e.response && e.response?.status === 404) {
            return dispatch({
                type: 'TPK_PAGE_DATA_RESPONSE',
                data: {
                    startDate: 0,
                    endDate: 0,
                    recaptcha: 'V3'
                }
            })
        }

        return dispatch(actionTPKError(Errors.INTERNAL_ERROR))
    } finally {
        dispatch(clearSigninRedirect())
    }
}

export const fetchTPKGoodiesData = () => async (dispatch: Dispatch<Action>) => {
    try {
        const response = await ws.get(endpoints.tuPreferesKing.goodies)
        dispatch({
            type: 'TPK_GOODIES_DATA_RESPONSE',
            data: response.data.goodies
        })
        return response.data
    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 404:
                    throw new Error(Errors.INVALID_CODE)
                case 400:
                    throw new Error(Errors.BAD_CHOICE)
                case 422:
                case 403:
                    break
                case 500:
                    throw new Error(Errors.INTERNAL_ERROR)
            }
        }
    }
}

export const searchAllRestaurants = () => async (
    dispatch: Dispatch<Action>
) => {
    try {
        const response = await ws.get(endpoints.storeLocator.all)
        dispatch({
            type: 'ALL_RESTAURANTS_DATA_RESPONSE',
            data: response.data.map(restaurant => {
                return {
                    name: restaurant.name,
                    frNumber: restaurant.id
                }
            })
        })
    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 404:
                    throw new Error(Errors.INVALID_CODE)
                case 400:
                    throw new Error(Errors.BAD_CHOICE)
                case 422:
                case 403:
                    break
                case 500:
                    throw new Error(Errors.INTERNAL_ERROR)
            }
        }
    }
}

export const fetchAvailability = (frNumber: string, goodieId: number) => async (
    dispatch: Dispatch<Action>
) => {
    try {
        const response = await ws.get(
            endpoints.tuPreferesKing.goodiesAvailability,
            { params: { gainId: goodieId, frNumber: frNumber } }
        )
        dispatch({
            type: 'AVAILABILITY_RESPONSE',
            data: response.data
        })
    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 404:
                    dispatch(actionTPKError(Errors.INVALID_CODE))
                    break
                case 500:
                    dispatch(actionTPKError(Errors.INTERNAL_ERROR))
                    break
                default:
                    dispatch(actionTPKError(Errors.INTERNAL_ERROR))
            }
        } else {
            dispatch(actionTPKError(Errors.INTERNAL_ERROR))
        }
    }
}

export const tryCode = async (
    code: string,
    recaptchaToken: string
): Promise<TokenResponseDTO> => {
    const payload: CodeTryPayloadDTO = {
        code,
        recaptchaToken
    }
    try {
        const response = await ws.post(endpoints.tuPreferesKing.duel, payload)
        return response.data
    } catch (e) {
        if (e.response) {
            switch (e.response.status) {
                case 400:
                case 404:
                    throw new Error(Errors.INVALID_CODE)
                case 423:
                    throw new Error(Errors.ALREADY_COLLECTED)
                case 498:
                    throw new Error(Errors.INVALID_CAPTCHA)
                case 500:
                    throw new Error(Errors.INTERNAL_ERROR)
                default:
                    throw new Error(Errors.INTERNAL_ERROR)
            }
        }
        throw new Error(Errors.INTERNAL_ERROR)
    }
}

export const selectWinning = (
    tpkToken: string,
    choice: 'CHILD' | 'PARENT'
) => async (dispatch: Dispatch<any>) => {
    try {
        await ws.post(endpoints.tuPreferesKing.choice, {
            tpkToken,
            choice
        })
    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 404:
                    throw new Error(Errors.INVALID_CODE)
                case 400:
                    throw new Error(Errors.BAD_CHOICE)
                case 422:
                case 403:
                    //422 and 403 just need a refetch
                    break
                case 500:
                    throw new Error(Errors.INTERNAL_ERROR)
            }
        }
    }
    await dispatch(fetchTPKDuelData(tpkToken))
}

export const collectWinning = (
    tpkToken: string,
    email: string,
    optinMarketingEmail: boolean
) => async (dispatch: Dispatch<any>, getState: () => RootState) => {
    try {
        const userToken = getState().userState.token
        const response = await ws.post(
            endpoints.tuPreferesKing.collect,
            {
                tpkToken,
                email,
                optinMarketingEmail
            },
            {
                headers: userToken
                    ? { RenewToken: true, Authorization: userToken }
                    : {}
            }
        )
        if (response.headers && response.headers.authorization) {
            dispatch(updateToken(response.headers.authorization))
        }
    } catch (error) {
        if (error.response) {
            switch (error.response.status) {
                case 404:
                    throw new Error(Errors.INVALID_CODE)
                case 400:
                    throw new Error(Errors.MISSING_EMAIL)
                case 422:
                case 403:
                    //422 and 403 just need a refetch
                    break
                case 500:
                    throw new Error(Errors.INTERNAL_ERROR)
            }
        }
    }
    await dispatch(fetchTPKDuelData(tpkToken))
}

export const resetState = () => (dispatch: Dispatch<any>) => {
    dispatch({
        type: 'TPK_RESET_DUEL'
    })
}

const actionTPKError = (error: ErrorType): ActionTPKError => {
    return {
        type: 'TPK_ERROR',
        error
    }
}

export type Action =
    | ActionTPKDuelDataResponse
    | ActionTPKPageDataResponse
    | ActionTPKReset
    | ActionsNetwork
    | ActionTPKError
    | ActionTPKGoodiesDataResponse
    | ActionTPKAllRestaurantsResponse
    | ActionTPKAvailabilityResponse
